import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export async function exportAccountListExcelReport({
    data,
    fileName = "Account_List_Report.xlsx",
    meta = {}
}) {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Account List");

    // Define styles
    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' }, size: 12 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };

    const titleStyle = {
        font: { bold: true, size: 16, color: { argb: '000000' } },
        alignment: { vertical: 'middle', horizontal: 'center' }
    };

    const metaStyle = {
        font: { bold: true, size: 11, color: { argb: '000000' } },
        alignment: { vertical: 'middle', horizontal: 'left' }
    };

    const dataHeaderStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '70AD47' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };

    const dataRowStyle = {
        alignment: { vertical: 'middle', horizontal: 'left' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };

    const alternateRowStyle = {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } }
    };

    let currentRow = 1;

    // Add Report Title
    const titleRow = sheet.getRow(currentRow++);
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, 8);
    const titleCell = titleRow.getCell(1);
    titleCell.value = "ACCOUNT LIST REPORT";
    Object.assign(titleCell, { style: titleStyle });

    // Add Metadata
    const metaRow1 = sheet.getRow(currentRow++);
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, 8);
    const metaCell1 = metaRow1.getCell(1);
    metaCell1.value = `Generated on: ${meta.reportDate || new Date().toLocaleDateString()}`;
    Object.assign(metaCell1, { style: metaStyle });

    const metaRow2 = sheet.getRow(currentRow++);
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, 8);
    const metaCell2 = metaRow2.getCell(1);
    metaCell2.value = `District: ${meta.districtName || 'N/A'} | Branch: ${meta.branchName || 'N/A'} | Status: ${meta.status || 'All Accounts'}`;
    Object.assign(metaCell2, { style: metaStyle });

    const metaRow3 = sheet.getRow(currentRow++);
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, 8);
    const metaCell3 = metaRow3.getCell(1);
    metaCell3.value = `Total Accounts: ${meta.totalAccounts || data.length}`;
    Object.assign(metaCell3, { style: metaStyle });

    // Empty row for spacing
    currentRow++;

    // Define column headers
    const headers = [
        { header: 'Account Number', key: 'accountNumber', width: 20 },
        { header: 'Product Name', key: 'productName', width: 25 },
        { header: 'Customer Number', key: 'customerNumber', width: 18 },
        { header: 'NIC Number', key: 'nicNumber', width: 18 },
        { header: 'Full Name', key: 'fullName', width: 35 },
        { header: 'Branch Name', key: 'branchName', width: 25 },
        { header: 'District Name', key: 'districtName', width: 20 },
        { header: 'Account Balance', key: 'accountBalance', width: 20 }
    ];

    // Add data headers
    const headerRow = sheet.getRow(currentRow++);
    headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header.header;
        Object.assign(cell, { style: dataHeaderStyle });
    });

    // Add data rows
    data.forEach((account, index) => {
        const dataRow = sheet.getRow(currentRow++);

        headers.forEach((header, colIndex) => {
            const cell = dataRow.getCell(colIndex + 1);
            const value = account[header.key];
            cell.value = (value === 0 || value) ? value : 'N/A';

            // Default style
            Object.assign(cell, { style: dataRowStyle });

            // Specific formatting for balance
            if (header.key === 'accountBalance') {
                cell.numFmt = '#,##0.00';
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            }

            // Apply alternate row styling
            if (index % 2 === 1) {
                cell.fill = alternateRowStyle.fill;
            }
        });
    });

    // Add summary section
    currentRow++;
    const summaryRow1 = sheet.getRow(currentRow++);
    sheet.mergeCells(currentRow - 1, 1, currentRow - 1, 8);
    const summaryCell1 = summaryRow1.getCell(1);
    summaryCell1.value = "REPORT SUMMARY";
    Object.assign(summaryCell1, { style: headerStyle });

    const summaryRow2 = sheet.getRow(currentRow++);
    summaryRow2.getCell(1).value = "Total Accounts:";
    summaryRow2.getCell(2).value = data.length;
    Object.assign(summaryRow2.getCell(1), { style: { font: { bold: true } } });
    Object.assign(summaryRow2.getCell(2), { style: { font: { bold: true } } });

    const summaryRow3 = sheet.getRow(currentRow++);
    summaryRow3.getCell(1).value = "Report Date:";
    summaryRow3.getCell(2).value = meta.reportDate || new Date().toLocaleDateString();
    Object.assign(summaryRow3.getCell(1), { style: { font: { bold: true } } });

    const summaryRow4 = sheet.getRow(currentRow++);
    summaryRow4.getCell(1).value = "Generated By:";
    summaryRow4.getCell(2).value = "System User";
    Object.assign(summaryRow4.getCell(1), { style: { font: { bold: true } } });

    // Apply column widths
    headers.forEach((header, index) => {
        sheet.getColumn(index + 1).width = header.width;
    });

    // Freeze headers (title + metadata + column headers)
    sheet.views = [{ state: 'frozen', ySplit: 6 }];

    // Generate and save the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    saveAs(blob, fileName);
}